import React, { useState, useEffect, useContext } from 'react';
import inventoryService from '../../services/inventoryService';
import api from '../../services/api';
import Modal from '../../comun/Modal';
import { AuthContext } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const Bodega = () => {
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_HELADERIA' || user?.is_superuser;

    const [activeTab, setActiveTab] = useState('insumos');
    const [variables, setVariables] = useState([]); // insumos, movimientos, recetas
    
    // Estados generales
    const [supplies, setSupplies] = useState([]);
    const [movements, setMovements] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estado para Modales
    const [isSupplyModalOpen, setIsSupplyModalOpen] = useState(false);
    const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
    const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
    const [importing, setImporting] = useState(false);
    
    // Formularios
    const [editingSupply, setEditingSupply] = useState(null);
    const [supplyForm, setSupplyForm] = useState({ 
        name: '', description: '', unit: '', is_active: true, is_production_item: false,
        purchase_quantity: 0, purchase_unit_cost: 0, yield_quantity: 0,
        yield_per_unit: 1, cost_per_unit: 0, current_stock: 0, stock_tarros: 0,
        is_paleta_converted: true
    });
    const [unitOptions, setUnitOptions] = useState([]);
    
    const [movementForm, setMovementForm] = useState({ supply: '', movement_type: 'in', quantity: 0, purchase_quantity: 0, purchase_unit_cost: 0, reason: '', lockedSupply: false });
    
    const [allRecipes, setAllRecipes] = useState([]);
    const [recipeForm, setRecipeForm] = useState({ id: null, product: '', supply: '', quantity: 0 });
    const [viewDetailsProductId, setViewDetailsProductId] = useState(null);

    // (Mezclas movido a su propia página /heladeria/mezclas)

    const fetchData = async () => {
        setLoading(true);
        try {
            const [suppliesData, productsData, optionsData, variablesData] = await Promise.all([
                inventoryService.getSupplies(),
                api.get('/api/menu/products/', { baseURL: process.env.REACT_APP_HELADERIA_SERVICE }),
                api.options('/api/menu/supplies/', { baseURL: process.env.REACT_APP_HELADERIA_SERVICE }),
                api.get('/api/menu/variables/', { baseURL: process.env.REACT_APP_HELADERIA_SERVICE })
            ]);
            setSupplies(suppliesData);
            setProducts(productsData.data?.results || productsData.data || []);
            setVariables(variablesData.data?.results || variablesData.data || []);
            
            if (optionsData.data?.actions?.POST?.unit?.choices) {
                setUnitOptions(optionsData.data.actions.POST.unit.choices);
                if (optionsData.data.actions.POST.unit.choices.length > 0 && !supplyForm.unit) {
                    setSupplyForm(prev => ({ ...prev, unit: optionsData.data.actions.POST.unit.choices[0].value }));
                }
            }
            
            if (activeTab === 'movimientos') {
                const movs = await inventoryService.getMovements();
                setMovements(movs);
            }
            
            if (activeTab === 'recetas') {
                const recs = await inventoryService.getRecipeIngredients();
                setAllRecipes(recs);
            }
        } catch (error) {
            console.error("Error fetching bodega data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const handleSupplySubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingSupply) {
                const stockPaletas = parseFloat(supplyForm.current_stock) || 0;
                const stockTarros = parseFloat(supplyForm.stock_tarros) || 0;
                let finalYield = parseFloat(supplyForm.yield_per_unit) || 1;
                
                if (stockTarros > 0) {
                    finalYield = stockPaletas / stockTarros;
                }
                
                await inventoryService.updateSupply(editingSupply.id, {
                    name: supplyForm.name,
                    description: supplyForm.description,
                    unit: supplyForm.unit,
                    is_active: supplyForm.is_active,
                    is_production_item: supplyForm.is_production_item,
                    current_stock: stockPaletas,
                    yield_per_unit: finalYield,
                    cost_per_unit: parseFloat(supplyForm.cost_per_unit) || 0
                });
            } else {
                const isPaleta = supplyForm.is_paleta_converted;
                const purchaseQty = parseFloat(supplyForm.purchase_quantity) || 0;
                const purchaseCost = parseFloat(supplyForm.purchase_unit_cost) || 0;
                const qtyPaletas = isPaleta ? (parseFloat(supplyForm.yield_quantity) || 0) : purchaseQty;
                
                const newSupply = await inventoryService.createSupply({ 
                    name: supplyForm.name, 
                    description: supplyForm.description,
                    unit: supplyForm.unit,
                    is_active: supplyForm.is_active,
                    is_production_item: supplyForm.is_production_item,
                    current_stock: 0 
                });
                
                if (qtyPaletas > 0 || purchaseQty > 0) {
                    await inventoryService.recordMovement(newSupply.id, {
                        movement_type: 'in',
                        quantity: qtyPaletas || 1,
                        purchase_quantity: purchaseQty,
                        purchase_unit_cost: purchaseCost,
                        reason: 'Stock inicial'
                    });
                }
            }
            setIsSupplyModalOpen(false);
            fetchData();
        } catch (error) {
            console.error(error);
            alert('Error al guardar insumo');
        }
    };
    
    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        setImporting(true);
        try {
            await api.post('/api/menu/supplies/import_excel/', formData, {
                baseURL: process.env.REACT_APP_HELADERIA_SERVICE,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert('Importación completada con éxito');
            fetchData();
        } catch (error) {
            console.error(error);
            alert('Error al importar Excel: ' + (error.response?.data?.error || error.message));
        } finally {
            setImporting(false);
            e.target.value = null; // reset file input
        }
    };

    const handleDeleteSupply = async (id) => {
        if(window.confirm('¿Eliminar este insumo? Se eliminarán también sus movimientos.')) {
            try {
                await inventoryService.deleteSupply(id);
                fetchData();
            } catch (error) {
                alert('Error al eliminar insumo');
            }
        }
    };

    // ---- HANDLERS MOVIMIENTOS ----
    const handleMovementSubmit = async (e) => {
        e.preventDefault();
        try {
            const selSupplyId = typeof movementForm.supply === 'string' ? parseInt(movementForm.supply) : movementForm.supply;
            const selSupply = supplies.find(s => s.id === selSupplyId);
            const isConverted = selSupply && parseFloat(selSupply.yield_per_unit || 1) !== 1;
            
            let finalQuantity = parseFloat(movementForm.quantity) || 0;
            if (movementForm.movement_type === 'in' && !isConverted) {
                finalQuantity = parseFloat(movementForm.purchase_quantity) || 0;
            }
            
            await inventoryService.recordMovement(movementForm.supply, {
                movement_type: movementForm.movement_type,
                quantity: finalQuantity,
                purchase_quantity: movementForm.movement_type === 'in' ? parseFloat(movementForm.purchase_quantity || 0) : null,
                purchase_unit_cost: movementForm.movement_type === 'in' ? parseFloat(movementForm.purchase_unit_cost || 0) : null,
                reason: movementForm.reason
            });
            setIsMovementModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Error al registrar movimiento. Verifique los datos.');
        }
    };

    // ---- HANDLERS RECETAS ----
    const loadAllRecipes = async () => {
        try {
            const data = await inventoryService.getRecipeIngredients();
            setAllRecipes(data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleRecipeSubmit = async (e) => {
        e.preventDefault();
        try {
            // Edit not implemented in service, but we can delete and create if needed, 
            // or if the service supports it we would use updateRecipeIngredient.
            // But wait, the service only has createRecipeIngredient and deleteRecipeIngredient.
            // So if editing, we delete the old and create the new.
            if (recipeForm.id) {
                await inventoryService.deleteRecipeIngredient(recipeForm.id);
            }
            await inventoryService.createRecipeIngredient({
                product: recipeForm.target_type === 'variable' ? null : recipeForm.product,
                supply: recipeForm.supply,
                quantity: parseFloat(recipeForm.quantity),
                size: null,
                variable_option: recipeForm.variable_option || null
            });
            setIsRecipeModalOpen(false);
            loadAllRecipes();
        } catch (error) {
            console.error(error.response?.data || error);
            const msg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            alert('Error al guardar receta: ' + msg);
        }
    };
    
    const handleDeleteRecipe = async (id) => {
        if(window.confirm('¿Eliminar esta receta?')) {
            try {
                await inventoryService.deleteRecipeIngredient(id);
                loadAllRecipes();
            } catch (error) {
                alert('Error al eliminar');
            }
        }
    }


    const getStockDisplay = (supplyId) => {
        const s = supplies.find(sp => sp.id === supplyId);
        if (!s) return { stock: 0, unit: '', color: 'gray' };
        return {
            stock: s.current_stock,
            unit: s.unit_display,
            color: s.current_stock <= 0 ? 'red' : 'green'
        };
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        doc.text('Reporte de Bodega e Insumos', 14, 15);
        const tableColumn = ["Nombre", "Unidad", "Stock Actual", "Estado"];
        const tableRows = [];
        
        supplies.forEach(s => {
            const data = [
                s.name,
                s.unit_display,
                Math.round(s.current_stock),
                s.is_active ? 'Activo' : 'Inactivo'
            ];
            tableRows.push(data);
        });
        
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 20
        });
        
        doc.save(`Inventario_${new Date().toLocaleDateString()}.pdf`);
    };

    const exportExcel = () => {
        const tableData = supplies.map(s => ({
            Nombre: s.name,
            Unidad: s.unit_display,
            'Stock Actual': Math.round(s.current_stock),
            Estado: s.is_active ? 'Activo' : 'Inactivo'
        }));

        const worksheet = XLSX.utils.json_to_sheet(tableData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Bodega");
        
        // Generar archivo Excel y descargar
        XLSX.writeFile(workbook, `Inventario_${new Date().toLocaleDateString()}.xlsx`);
    };

    if (loading && supplies.length === 0) return <div>Cargando Bodega...</div>;

    return (
        <div className="page-container">
            <div className="page-header">
                <h2>Bodega e Insumos</h2>
            </div>

            <div className="tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '10px' }}>
                <button className={`btn ${activeTab === 'insumos' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('insumos')}>Insumos</button>
                {isAdmin && <button className={`btn ${activeTab === 'movimientos' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('movimientos')}>Movimientos</button>}
                <button className={`btn ${activeTab === 'recetas' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('recetas')}>Recetas (Descuentos)</button>
            </div>

            {/* TAB INSUMOS */}
            {activeTab === 'insumos' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', gap: '10px' }}>
                        {isAdmin && (
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <input type="file" id="import-excel" accept=".xlsx, .xls" style={{ display: 'none' }} onChange={handleImportExcel} disabled={importing} />
                                <label htmlFor="import-excel" className="btn btn-warning" style={{ margin: 0, cursor: importing ? 'not-allowed' : 'pointer' }}>
                                    {importing ? 'Importando...' : <><i className="bi bi-upload"></i> Importar Excel</>}
                                </label>
                            </div>
                        )}
                        <button className="btn btn-success" onClick={exportExcel} title="Exportar a Excel (.xlsx)">
                            <i className="bi bi-file-earmark-excel"></i> Excel
                        </button>
                        <button className="btn btn-danger" onClick={exportPDF} title="Exportar a PDF">
                            <i className="bi bi-file-earmark-pdf"></i> PDF
                        </button>
                        <button className="btn btn-primary" onClick={() => {
                            setEditingSupply(null);
                            setSupplyForm({ 
                                name: '', description: '', unit: unitOptions.length ? unitOptions[0].value : '', 
                                is_active: true, is_production_item: false,
                                purchase_quantity: 0, purchase_unit_cost: 0, yield_quantity: 0,
                                is_paleta_converted: true
                            });
                            setIsSupplyModalOpen(true);
                        }}>Nuevo Insumo</button>
                    </div>
                    
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Unidad</th>
                                    {isAdmin && <th>Stock (Paletas)</th>}
                                    {isAdmin && <th>Stock (Tarros)</th>}
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {supplies.length === 0 ? <tr><td colSpan="7">No hay insumos</td></tr> : supplies.map(s => (
                                    <tr key={s.id}>
                                        <td>{s.name}</td>
                                        <td>{s.unit_display}</td>
                                        {isAdmin && <td style={{ fontWeight: 'bold', color: s.current_stock <= 0 ? 'red' : 'green'}}>{parseFloat(s.current_stock)}</td>}
                                        {isAdmin && <td style={{ color: '#666' }}>{s.yield_per_unit ? (s.current_stock / s.yield_per_unit).toFixed(2) : '-'}</td>}
                                        <td>
                                            {s.is_active ? 'Activo' : 'Inactivo'}
                                            {s.is_production_item && <span style={{ marginLeft: '4px', fontSize: '11px', background: '#0d6efd', color: '#fff', padding: '1px 6px', borderRadius: '8px' }}>Mezcla</span>}
                                        </td>
                                        <td>
                                            <button className="btn btn-secondary" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => {
                                                setEditingSupply(s);
                                                setSupplyForm({ 
                                                    name: s.name, description: s.description, unit: s.unit, 
                                                    is_active: s.is_active, is_production_item: s.is_production_item || false,
                                                    yield_per_unit: s.yield_per_unit || 1, cost_per_unit: s.cost_per_unit || 0,
                                                    current_stock: s.current_stock || 0,
                                                    stock_tarros: s.yield_per_unit ? (s.current_stock / s.yield_per_unit).toFixed(3) : 0,
                                                    is_paleta_converted: parseFloat(s.yield_per_unit || 1) !== 1
                                                });
                                                setIsSupplyModalOpen(true);
                                            }}>Editar</button>
                                            
                                            {isAdmin && (
                                                <button className="btn btn-danger" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => handleDeleteSupply(s.id)}>Eliminar</button>
                                            )}
                                            
                                            <button className="btn btn-success" style={{ marginRight: '5px', padding: '0.25rem' }} title="Agregar Stock" onClick={() => {
                                                setMovementForm({ supply: s.id, movement_type: 'in', quantity: 0, purchase_quantity: 0, purchase_unit_cost: 0, reason: '', lockedSupply: true });
                                                setIsMovementModalOpen(true);
                                            }}><i className="bi bi-arrow-up"></i></button>
                                            
                                            {isAdmin && (
                                                <button className="btn btn-warning" style={{ padding: '0.25rem' }} title="Reducir Stock" onClick={() => {
                                                    setMovementForm({ supply: s.id, movement_type: 'out', quantity: 0, purchase_quantity: 0, purchase_unit_cost: 0, reason: '', lockedSupply: true });
                                                    setIsMovementModalOpen(true);
                                                }}><i className="bi bi-arrow-down"></i></button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB MOVIMIENTOS */}
            {activeTab === 'movimientos' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <button className="btn btn-primary" onClick={() => {
                            setMovementForm({ supply: supplies[0]?.id || '', movement_type: 'in', quantity: 0, purchase_quantity: 0, purchase_unit_cost: 0, reason: '', lockedSupply: false });
                            setIsMovementModalOpen(true);
                        }}>Registrar Movimiento</button>
                    </div>
                    
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Insumo</th>
                                    <th>Tipo</th>
                                    <th>Cantidad</th>
                                    <th>Motivo / Ref</th>
                                    <th>Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movements.length === 0 ? <tr><td colSpan="6">No hay movimientos</td></tr> : movements.map(m => (
                                    <tr key={m.id}>
                                        <td>{new Date(m.created_at).toLocaleString()}</td>
                                        <td>{m.supply_name}</td>
                                        <td>
                                            <span className={`tag ${m.movement_type === 'in' ? 'tag-green' : m.movement_type === 'out' ? 'tag-red' : 'tag-gray'}`}>
                                                {m.movement_type_display}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 'bold', color: m.movement_type === 'in' ? 'green' : (m.movement_type === 'out' || m.movement_type === 'sale' ? 'red' : 'inherit') }}>
                                            {m.movement_type === 'in' ? '+' : (m.movement_type === 'out' || m.movement_type === 'sale' ? '-' : '')}{parseFloat(m.quantity)}
                                        </td>
                                        <td>{m.reason} {m.reference_id && `(Ref: ${m.reference_id})`}</td>
                                        <td>{m.created_by}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB RECETAS */}
            {activeTab === 'recetas' && (() => {
                const productsWithRecipes = [...new Set(allRecipes.filter(r => r.product).map(r => r.product))].map(productId => {
                    return {
                        product: productId,
                        product_name: allRecipes.find(r => r.product === productId).product_name,
                        recipes: allRecipes.filter(r => r.product === productId)
                    }
                });
                
                const variablesWithRecipes = [...new Set(allRecipes.filter(r => !r.product && r.variable_option_name).map(r => r.variable_option_name))].map(varName => {
                    return {
                        variable_name: varName,
                        recipes: allRecipes.filter(r => !r.product && r.variable_option_name === varName)
                    }
                });

                return (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <button className="btn btn-primary" onClick={() => {
                            setRecipeForm({ id: null, target_type: 'product', product: products[0]?.id || '', supply: supplies[0]?.id || '', quantity: 0, variable_option: '' });
                            setIsRecipeModalOpen(true);
                        }}>Nueva Receta</button>
                    </div>
                    <div className="table-responsive">
                        <h4>Recetas por Producto</h4>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Producto Principal</th>
                                    <th>Insumos Configurados</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productsWithRecipes.length === 0 ? <tr><td colSpan="3">No hay productos con recetas configuradas.</td></tr> : productsWithRecipes.map(p => (
                                    <tr key={p.product}>
                                        <td>{p.product_name}</td>
                                        <td>{p.recipes.length} insumo(s)</td>
                                        <td>
                                            <button className="btn btn-primary" style={{ padding: '0.25rem' }} onClick={() => setViewDetailsProductId(p.product)}>Ver Detalles / Editar</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    {variablesWithRecipes.length > 0 && (
                        <div className="table-responsive" style={{ marginTop: '2rem' }}>
                            <h4>Recetas de Variables Globales</h4>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Opción de Variable</th>
                                        <th>Insumos Configurados</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {variablesWithRecipes.map(v => (
                                        <tr key={v.variable_name}>
                                            <td>{v.variable_name}</td>
                                            <td>{v.recipes.length} insumo(s)</td>
                                            <td>
                                                <button className="btn btn-primary" style={{ padding: '0.25rem' }} onClick={() => setViewDetailsProductId(`var_${v.variable_name}`)}>Ver Detalles / Editar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                );
            })()}

            {/* FIN TABLAS - Mezclas movido a su propia página /heladeria/mezclas */}

            {/* MODALS */}
            {/* Modal Insumo */}
            <Modal isOpen={isSupplyModalOpen} onClose={() => setIsSupplyModalOpen(false)} title={editingSupply ? 'Editar Insumo' : 'Nuevo Insumo'}>
                <form onSubmit={handleSupplySubmit}>
                    <div className="form-group">
                        <label>Nombre del Insumo</label>
                        <input type="text" required value={supplyForm.name} onChange={e => setSupplyForm({...supplyForm, name: e.target.value})} />
                    </div>
                    <div className="form-group">
                        <label>Unidad de Medida (Referencial)</label>
                        <select className="form-control" value={supplyForm.unit} onChange={e => setSupplyForm({...supplyForm, unit: e.target.value})}>
                            {unitOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.display_name}</option>
                            ))}
                        </select>
                    </div>
                    
                    {!editingSupply ? (
                        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px', border: '1px solid #dee2e6' }}>
                            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                                <input type="checkbox" id="is_paleta_converted"
                                    checked={supplyForm.is_paleta_converted}
                                    onChange={e => setSupplyForm({...supplyForm, is_paleta_converted: e.target.checked})} />
                                <label htmlFor="is_paleta_converted" style={{ margin: 0, fontWeight: 'bold' }}>
                                    Este insumo se transforma en Paletas (Ej: Leche, Chocolate)
                                </label>
                            </div>
                            
                            <h6 style={{ margin: '0 0 10px 0', color: '#495057' }}>Datos de Compra y Rendimiento Inicial</h6>
                            <div className="form-group">
                                <label>QTY Comprada ({supplyForm.is_paleta_converted ? 'Ej. Tarros/Cajas' : 'Unidades'})</label>
                                <input type="number" step="0.01" value={supplyForm.purchase_quantity} onChange={e => setSupplyForm({...supplyForm, purchase_quantity: e.target.value})} />
                            </div>
                            <div className="form-group">
                                <label>Precio por {supplyForm.is_paleta_converted ? 'Tarro/Caja' : 'Unidad'} ($)</label>
                                <input type="number" step="0.01" value={supplyForm.purchase_unit_cost} onChange={e => setSupplyForm({...supplyForm, purchase_unit_cost: e.target.value})} />
                            </div>
                            {supplyForm.is_paleta_converted && (
                                <div className="form-group">
                                    <label>Rendimiento Total (QTY Paletas que salen)</label>
                                    <input type="number" step="0.01" value={supplyForm.yield_quantity} onChange={e => setSupplyForm({...supplyForm, yield_quantity: e.target.value})} />
                                    <small>El sistema dividirá esto automáticamente y actualizará tu stock en paletas y tarros.</small>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px', border: '1px solid #dee2e6' }}>
                            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                                <input type="checkbox" id="is_paleta_converted_edit"
                                    checked={supplyForm.is_paleta_converted}
                                    onChange={e => setSupplyForm({
                                        ...supplyForm, 
                                        is_paleta_converted: e.target.checked,
                                        yield_per_unit: e.target.checked ? supplyForm.yield_per_unit : 1
                                    })} />
                                <label htmlFor="is_paleta_converted_edit" style={{ margin: 0, fontWeight: 'bold' }}>
                                    Este insumo se transforma en Paletas (Ej: Leche, Chocolate)
                                </label>
                            </div>
                            
                            <h6 style={{ margin: '0 0 10px 0', color: '#495057' }}>Ajustes Manuales de Inventario y Conversión</h6>
                            
                            {supplyForm.is_paleta_converted ? (
                                <>
                                    <small style={{ display: 'block', marginBottom: '10px' }}>
                                        Si el stock se descuadró, puedes forzar los valores exactos aquí. El rendimiento se recalculará automáticamente.
                                    </small>
                                    
                                    <div className="form-group" style={{ display: 'flex', gap: '15px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label>Stock (Paletas) <span style={{ color: 'red' }}>*</span></label>
                                            <input type="number" step="0.01" required value={supplyForm.current_stock} 
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setSupplyForm(prev => ({
                                                        ...prev, 
                                                        current_stock: val,
                                                        stock_tarros: prev.yield_per_unit ? (val / prev.yield_per_unit).toFixed(3) : 0
                                                    }));
                                                }} 
                                            />
                                            <small>Lo que realmente vendes</small>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label>Stock Equivalente (Tarros)</label>
                                            <input type="number" step="0.01" value={supplyForm.stock_tarros} 
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setSupplyForm(prev => ({
                                                        ...prev, 
                                                        stock_tarros: val,
                                                        current_stock: val * prev.yield_per_unit
                                                    }));
                                                }} 
                                            />
                                            <small>Unidades físicas (Calculado: {(parseFloat(supplyForm.current_stock || 0) / (parseFloat(supplyForm.stock_tarros || 1) || 1)).toFixed(2)} paletas/tarro)</small>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="form-group">
                                    <label>Stock Actual (Unidades)</label>
                                    <input type="number" step="0.01" required value={supplyForm.current_stock} 
                                        onChange={e => {
                                            const val = e.target.value;
                                            setSupplyForm(prev => ({
                                                ...prev, 
                                                current_stock: val,
                                                stock_tarros: val
                                            }));
                                        }} 
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label>Costo Exacto por Paleta ($)</label>
                                <input type="number" step="0.001" value={supplyForm.cost_per_unit} onChange={e => setSupplyForm({...supplyForm, cost_per_unit: e.target.value})} />
                            </div>
                        </div>
                    )}
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" id="is_production_item"
                            checked={supplyForm.is_production_item}
                            onChange={e => setSupplyForm({...supplyForm, is_production_item: e.target.checked})} />
                        <label htmlFor="is_production_item" style={{ margin: 0 }}>
                            Producto de mezcla (no aparece en POS, solo se gestiona desde Mezclas)
                        </label>
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsSupplyModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar</button>
                    </div>
                </form>
            </Modal>

            {/* Modal Movimiento */}
            <Modal isOpen={isMovementModalOpen} onClose={() => setIsMovementModalOpen(false)} title="Registrar Movimiento de Bodega">
                <form onSubmit={handleMovementSubmit}>
                    <div className="form-group">
                        <label>Insumo</label>
                        <select className="form-control" required value={movementForm.supply} onChange={e => setMovementForm({...movementForm, supply: e.target.value})} disabled={movementForm.lockedSupply}>
                            {supplies.map(s => <option key={s.id} value={s.id}>{s.name} {isAdmin ? `(Stock actual: ${s.current_stock})` : ''}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Tipo de Movimiento</label>
                        <select className="form-control" value={movementForm.movement_type} onChange={e => setMovementForm({...movementForm, movement_type: e.target.value})}>
                            <option value="in">Ingreso (Sumar al stock)</option>
                            {isAdmin && (
                                <>
                                    <option value="out">Egreso (Restar al stock)</option>
                                    <option value="adjustment">Ajuste (Reemplazar stock exacto)</option>
                                </>
                            )}
                        </select>
                    </div>
                    {movementForm.movement_type === 'in' ? (() => {
                        const selSupplyId = typeof movementForm.supply === 'string' ? parseInt(movementForm.supply) : movementForm.supply;
                        const selSupply = supplies.find(s => s.id === selSupplyId);
                        const isConverted = selSupply && parseFloat(selSupply.yield_per_unit || 1) !== 1;
                        
                        return (
                            <>
                                <div className="form-group">
                                    <label>QTY Comprada ({isConverted ? 'Ej. Tarros/Cajas' : 'Unidades'})</label>
                                    <input type="number" step="0.01" required value={movementForm.purchase_quantity} onChange={e => setMovementForm({...movementForm, purchase_quantity: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>Precio por {isConverted ? 'Tarro' : 'Unidad'} ($)</label>
                                    <input type="number" step="0.01" required value={movementForm.purchase_unit_cost} onChange={e => setMovementForm({...movementForm, purchase_unit_cost: e.target.value})} />
                                </div>
                                {isConverted && (
                                    <div className="form-group">
                                        <label>Rendimiento Total (Paletas que salen de esta compra)</label>
                                        <input type="number" step="0.01" required value={movementForm.quantity} onChange={e => setMovementForm({...movementForm, quantity: e.target.value})} />
                                        <small>Esta es la cantidad que se sumará a tu stock real de venta.</small>
                                    </div>
                                )}
                                <div className="form-group" style={{ background: '#f8f9fa', padding: '10px', borderRadius: '5px' }}>
                                    <label style={{ marginBottom: 0 }}><strong>Costo Total:</strong> ${(parseFloat(movementForm.purchase_quantity || 0) * parseFloat(movementForm.purchase_unit_cost || 0)).toFixed(2)}</label>
                                </div>
                            </>
                        );
                    })() : (
                        <div className="form-group">
                            <label>Cantidad (Permite decimales)</label>
                            <input type="number" step="0.01" required value={movementForm.quantity} onChange={e => setMovementForm({...movementForm, quantity: e.target.value})} />
                        </div>
                    )}
                    <div className="form-group">
                        <label>Motivo</label>
                        <input type="text" required value={movementForm.reason} onChange={e => setMovementForm({...movementForm, reason: e.target.value})} placeholder="Ej: Compra a proveedor, daño..." />
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsMovementModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Registrar</button>
                    </div>
                </form>
            </Modal>

            {/* Modal Detalles Receta */}
            {viewDetailsProductId && (() => {
                let viewDetailsProduct = null;
                if (typeof viewDetailsProductId === 'string' && viewDetailsProductId.startsWith('var_')) {
                    const varName = viewDetailsProductId.replace('var_', '');
                    viewDetailsProduct = {
                        product: viewDetailsProductId,
                        product_name: `Variable Global: ${varName}`,
                        recipes: allRecipes.filter(r => !r.product && r.variable_option_name === varName)
                    };
                } else {
                    viewDetailsProduct = [...new Set(allRecipes.filter(r => r.product).map(r => r.product))]
                        .map(productId => ({
                            product: productId,
                            product_name: allRecipes.find(r => r.product === productId).product_name,
                            recipes: allRecipes.filter(r => r.product === productId)
                        })).find(p => p.product === viewDetailsProductId);
                }

                if (!viewDetailsProduct) return null;

                return (
                    <Modal isOpen={true} onClose={() => setViewDetailsProductId(null)} title={`Detalles de Receta: ${viewDetailsProduct.product_name}`} maxWidth="800px">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                            <button className="btn btn-primary" onClick={() => {
                                if (viewDetailsProductId.toString().startsWith('var_')) {
                                    setRecipeForm({ id: null, target_type: 'variable', product: '', supply: supplies[0]?.id || '', quantity: 0, variable_option: viewDetailsProduct.recipes[0]?.variable_option || '' });
                                } else {
                                    setRecipeForm({ id: null, target_type: 'product', product: viewDetailsProduct.product, supply: supplies[0]?.id || '', quantity: 0, variable_option: '' });
                                }
                                setIsRecipeModalOpen(true);
                            }}>Agregar Insumo a esta Receta</button>
                        </div>
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Insumo</th>
                                        <th>Cantidad</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewDetailsProduct.recipes.map(r => (
                                        <tr key={r.id}>
                                            <td>
                                                {r.supply_name}
                                                {r.variable_option_name && r.product && <span style={{display: 'block', fontSize: '0.85em', color: '#666'}}>Aplica en: {r.variable_option_name}</span>}
                                            </td>
                                            <td>{parseFloat(r.quantity)} {r.supply_unit}</td>
                                            <td>
                                                <button className="btn btn-secondary" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => {
                                                    setRecipeForm({ id: r.id, target_type: r.product ? 'product' : 'variable', product: r.product || '', supply: r.supply, quantity: r.quantity, variable_option: r.variable_option || '' });
                                                    setIsRecipeModalOpen(true);
                                                }}>Editar</button>
                                                <button className="btn btn-danger" style={{ padding: '0.25rem' }} onClick={() => handleDeleteRecipe(r.id)}>Eliminar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Modal>
                );
            })()}

            {/* Modal Receta */}
            <Modal isOpen={isRecipeModalOpen} onClose={() => setIsRecipeModalOpen(false)} title={recipeForm.id ? "Editar Receta" : "Nueva Receta"}>
                <form onSubmit={handleRecipeSubmit}>
                    <div className="form-group">
                        <label>Tipo de Receta</label>
                        <select className="form-control" value={recipeForm.target_type || 'product'} onChange={e => setRecipeForm({...recipeForm, target_type: e.target.value, product: '', variable_option: ''})}>
                            <option value="product">Para un Producto del Menú</option>
                            <option value="variable">Para una Variable Global (Ej. Coberturas, Bases)</option>
                        </select>
                    </div>

                    {recipeForm.target_type !== 'variable' ? (
                        <div className="form-group">
                            <label>Producto del Menú</label>
                            <select className="form-control" required value={recipeForm.product} onChange={e => setRecipeForm({...recipeForm, product: e.target.value, variable_option: ''})}>
                                <option value="">Seleccione un producto...</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                    ) : (
                        <div className="form-group">
                            <label>Opción de Variable Global</label>
                            <select className="form-control" required value={recipeForm.variable_option || ''} onChange={e => setRecipeForm({...recipeForm, variable_option: e.target.value})}>
                                <option value="">Seleccione una opción...</option>
                                {variables.map(v => (
                                    <optgroup key={v.id} label={v.name}>
                                        {v.options.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            <small>Esta receta descontará el insumo SIEMPRE que se elija esta opción en CUALQUIER producto.</small>
                        </div>
                    )}
                    <div className="form-group">
                        <label>Insumo a descontar</label>
                        <select className="form-control" required value={recipeForm.supply} onChange={e => setRecipeForm({...recipeForm, supply: e.target.value})}>
                            {supplies.map(s => <option key={s.id} value={s.id}>{s.name} ({s.unit_display})</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Cantidad a descontar por cada venta</label>
                        <input type="number" step="0.01" required value={recipeForm.quantity} onChange={e => setRecipeForm({...recipeForm, quantity: e.target.value})} />
                    </div>
                    {recipeForm.target_type !== 'variable' && (() => {
                        const selProduct = products.find(p => p.id === recipeForm.product);
                        if (selProduct && selProduct.variables && selProduct.variables.length > 0) {
                            return (
                                <div className="form-group">
                                    <label>Aplica solo en esta Opción (Opcional)</label>
                                    <select className="form-control" value={recipeForm.variable_option || ''} onChange={e => setRecipeForm({...recipeForm, variable_option: e.target.value})}>
                                        <option value="">Aplica a todas las ventas (Base)</option>
                                        {selProduct.variables.map(v => 
                                            v.options.map(opt => (
                                                <option key={opt.id} value={opt.id}>{v.name}: {opt.name}</option>
                                            ))
                                        )}
                                    </select>
                                    <small>Si eliges una opción, este insumo se descontará SÓLO cuando el cliente elija esa opción en este producto.</small>
                                </div>
                            );
                        }
                        return null;
                    })()}
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsRecipeModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar</button>
                    </div>
                </form>
            </Modal>

        </div>
    );
};

export default Bodega;
